/**
 * Permanent unit names on the map — client round, 3 October 2026.
 *
 * Zoomed in far enough, every visible watershed and country writes its own name across
 * itself instead of only answering a hover. The thresholds and the "who owns the label"
 * rule live in `hierarchy.ts` (`labelsAtZoom`, `resultLabelLevel`); this module is only
 * the Leaflet half, shared by the two places that draw those polygons —
 * `HierarchyLayers.tsx` (the §F5.8 rows) and `ResultLayers.tsx` (a use case's `units` or
 * `choropleth` layer).
 *
 * **A standalone tooltip, not a bound one.** Both call sites already `bindTooltip` on
 * every feature for the hover card, and Leaflet keeps exactly one tooltip per layer — a
 * permanent label bound to the same path would replace the card the client asked for last
 * month. So the labels are their own `L.Tooltip` objects at the unit's centroid, collected
 * in one `L.LayerGroup` so a zoom that crosses the threshold adds or removes them in a
 * single map operation rather than feature by feature.
 *
 * `interactive: false` (plus `pointer-events: none` in `base.css`): a label is a drawn
 * name, never a target. Clicks, hovers and the §F5.7 selection must all pass straight
 * through it to the polygon underneath, or a reader would find the middle of every
 * watershed unclickable.
 */
import L from 'leaflet';

import type { UnitsFC } from '../../lib/api';
import { unitCentroid } from '../../lib/geo';
import { escapeHtml } from '../../lib/html';

/** The class `base.css` styles — small semibold text with a white halo. */
export const UNIT_LABEL_CLASS = 'unit-label';

/**
 * One `L.LayerGroup` of centroid labels for these features. Add it to the map; drop it on
 * the way out.
 *
 * Placement is §F5.5's own rule (`lib/geo.ts::unitCentroid`): the API's
 * `properties.centroid` — PostGIS `ST_PointOnSurface`, which is guaranteed to be *inside*
 * the polygon — falling back to §G7's mean of the largest ring when the payload omits it.
 * A feature with neither a name nor a placeable point is skipped rather than labelled at
 * `[0, 0]`.
 */
export function createUnitLabels(features: UnitsFC['features']): L.LayerGroup {
  const group = L.layerGroup();
  for (const feature of features) {
    const name: unknown = feature.properties?.name;
    if (typeof name !== 'string' || !name) continue;
    const at = unitCentroid(feature.properties?.centroid, feature.geometry);
    if (!at) continue;
    L.tooltip({
      permanent: true,
      direction: 'center',
      className: UNIT_LABEL_CLASS,
      interactive: false,
      opacity: 1,
      offset: [0, 0],
    })
      .setLatLng(at)
      // `name` is `/hierarchy/units` text reaching `innerHTML` — see `lib/html.ts`.
      .setContent(escapeHtml(name))
      .addTo(group);
  }
  return group;
}

export default createUnitLabels;
